import type { GbpConfig, PostMedia } from '../../../types/publishing';
import { PostMediaView } from './PostMediaView';
import { LinkifiedText } from './LinkifiedText';

// Publishing preview — Google Business Profile post. Text + single image (no
// video), with the topic-type badge (STANDARD/EVENT/OFFER), the CTA button, and
// event/offer detail lines.

const CTA_LABEL: Record<string, string> = {
  LEARN_MORE: 'Learn more',
  BOOK: 'Book',
  ORDER: 'Order online',
  SHOP: 'Shop',
  SIGN_UP: 'Sign up',
  CALL: 'Call now',
};

export function GbpPreview({
  businessName,
  content,
  media,
  gbp,
}: {
  businessName: string;
  content: string;
  media: PostMedia[];
  gbp: GbpConfig;
}) {
  const image = media.filter((m) => m.type === 'image').slice(0, 1);
  return (
    <article className="mx-auto max-w-[380px] w-full rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <header className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
            G
          </div>
          <p className="text-sm font-semibold text-slate-900 truncate">{businessName}</p>
        </div>
        {gbp.topicType !== 'STANDARD' && (
          <span className="text-[10px] font-semibold uppercase tracking-wide rounded bg-slate-100 px-2 py-0.5 text-slate-600">
            {gbp.topicType}
          </span>
        )}
      </header>

      {/* Feed accepts varied aspect ratios — size to the image's own ratio (no
          fixed box, no letterbox bars); aspectClass is only the empty placeholder. */}
      {image.length > 0 && <PostMediaView media={image} aspectClass="aspect-[4/3]" fit="natural" />}

      {gbp.topicType === 'EVENT' && gbp.event && (
        <div className="px-4 pt-3">
          <p className="text-sm font-semibold text-slate-900">{gbp.event.title}</p>
          <p className="text-xs text-slate-500">
            {formatGbpDate(gbp.event.startDate)}
            {gbp.event.endDate ? ` – ${formatGbpDate(gbp.event.endDate)}` : ''}
          </p>
        </div>
      )}
      {gbp.topicType === 'OFFER' && gbp.offer?.couponCode && (
        <div className="px-4 pt-3">
          <span className="inline-block rounded border border-dashed border-slate-300 px-2 py-1 text-xs font-mono text-slate-700">
            {gbp.offer.couponCode}
          </span>
        </div>
      )}

      {content.trim() !== '' && (
        <p className="px-4 py-3 text-sm text-slate-800 whitespace-pre-wrap">
          <LinkifiedText text={content} />
        </p>
      )}

      {gbp.cta && (
        <div className="px-4 pb-4">
          <span className="inline-block rounded-full border border-blue-600 px-4 py-1.5 text-xs font-semibold text-blue-600">
            {CTA_LABEL[gbp.cta.type] ?? gbp.cta.type}
          </span>
        </div>
      )}
    </article>
  );
}

function formatGbpDate(d: { year: number; month: number; day: number }): string {
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${d.year}-${p2(d.month)}-${p2(d.day)}`;
}
